import React, { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { Slider, SliderTrack, SliderThumb, SliderOutput } from 'react-aria-components';
import type { RangeFilterProps, RangeValue } from '@/types/searchSide';
const ClearButton: React.FC<{
  onClear: () => void;
  min: number;
  max: number;
  formatValue?: (value: number) => string;
}> = ({
  onClear,
  min,
  max,
  formatValue = val => val.toLocaleString()
}) => <button type="button" className="t-inline-flex t-items-center t-gap-1 t-text-xs t-font-semibold t-text-automotive-primary hover:t-text-automotive-secondary t-transition-colors focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 t-rounded" onClick={onClear} aria-label={`Clear range filter (${formatValue(min)} - ${formatValue(max)})`}>
        <svg className="t-w-3.5 t-h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        <span>Clear range</span>
    </button>;
export const RangeFilter: React.FC<RangeFilterProps & {
  isOpen: boolean;
  onToggle: () => void;
}> = ({
  title,
  min,
  max,
  value,
  step,
  onChange,
  onClear,
  formatValue = val => val.toLocaleString(),
  isOpen,
  onToggle,
  className = "",
  'aria-label': ariaLabel
}) => {
  // Refs to directly update input values during slider drag (more performant)
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);
  const isSliderUpdatingRef = useRef(false);
  const sectionId = useId();
  const titleSlug = useMemo(() => title.toLowerCase().replace(/\s+/g, '-'), [title]);
  const triggerId = `${titleSlug}-${sectionId}-trigger`;
  const groupId = `${titleSlug}-${sectionId}-options`;
  const descriptionId = `${titleSlug}-${sectionId}-description`;
  const minInputId = `${titleSlug}-${sectionId}-min`;
  const maxInputId = `${titleSlug}-${sectionId}-max`;

  // Determine steps for different filter types
  const inputStep = useMemo(() => {
    if (title.toLowerCase().includes('year')) return 1;
    if (title.toLowerCase().includes('payment')) return 100;
    return 1000;
  }, [title]);

  // Sync input values when state changes (e.g., filters cleared, URL params loaded)
  useEffect(() => {
    if (!isSliderUpdatingRef.current) {
      if (minInputRef.current) {
        minInputRef.current.value = value[0].toString();
      }
      if (maxInputRef.current) {
        maxInputRef.current.value = value[1].toString();
      }
    }
    isSliderUpdatingRef.current = false;
  }, [value]);

  // Handle slider changes - update inputs in real-time via refs
  const handleSliderChange = useCallback((newValues: number[]) => {
    const adjustedValues = newValues.map(v => Math.round(v / step) * step);
    if (minInputRef.current && adjustedValues[0] !== undefined) {
      minInputRef.current.value = adjustedValues[0].toString();
    }
    if (maxInputRef.current && adjustedValues[1] !== undefined) {
      maxInputRef.current.value = adjustedValues[1].toString();
    }
  }, [step]);

  // Handle slider changes - only update URL state when dragging ends
  const handleSliderChangeEnd = useCallback((newValues_0: number[]) => {
    const adjustedValues_0 = newValues_0.map(v_0 => Math.round(v_0 / step) * step) as RangeValue;
    isSliderUpdatingRef.current = true;
    onChange(adjustedValues_0);
  }, [onChange, step]);

  // Handle input changes
  const handleMinInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value);
    if (newValue >= min && newValue <= value[1]) {
      const adjustedValue = Math.floor(newValue / inputStep) * inputStep;
      onChange([adjustedValue, value[1]] as RangeValue);
    }
  }, [min, value, inputStep, onChange]);
  const handleMaxInputChange = useCallback((event_0: React.ChangeEvent<HTMLInputElement>) => {
    const newValue_0 = Number(event_0.target.value);
    if (newValue_0 <= max && newValue_0 >= value[0]) {
      const adjustedValue_0 = Math.ceil(newValue_0 / inputStep) * inputStep;
      onChange([value[0], adjustedValue_0] as RangeValue);
    }
  }, [max, value, inputStep, onChange]);
  const hasActiveRange = value[0] !== min || value[1] !== max;
  return <div className={className} aria-label={ariaLabel}>
            <div>
                <div>
                    <button id={triggerId} className="t-flex t-w-full t-items-center t-justify-between t-text-left t-text-sm t-font-extrabold t-text-[#38323d] t-transition-colors hover:t-text-brand-violet focus:t-rounded focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2" type="button" onClick={onToggle} aria-expanded={isOpen} aria-controls={groupId}>
                        <span>{title}</span>
                        <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} className="t-h-4 t-w-4 t-text-[#928c96]" aria-hidden="true" />
                    </button>
                    {hasActiveRange && <div className="t-mt-2">
                            <ClearButton onClear={onClear} min={value[0]} max={value[1]} formatValue={formatValue} />
                        </div>}
                </div>

                {isOpen && <div id={groupId} className="t-mt-4 t-space-y-3" role="group" aria-labelledby={triggerId} aria-describedby={descriptionId}>
                        <p id={descriptionId} className="t-sr-only">
                            Adjust the minimum and maximum {title.toLowerCase()} values using the inputs or slider.
                        </p>

                        {/* Modern Input Fields */}
                        <div className="t-grid t-grid-cols-1 t-gap-3 sm:t-grid-cols-2 sm:t-items-end">
                            <div className="t-min-w-0">
                                <label htmlFor={minInputId} className="t-mb-1 t-block t-text-[11px] t-font-medium t-text-[#69636f]">
                                    Min {title}
                                </label>
                                <div className="t-flex t-min-w-0">
                                    <input id={minInputId} ref={minInputRef} type="number" min={min} max={value[1]} step={inputStep} inputMode="numeric" enterKeyHint="done" defaultValue={value[0]} onChange={handleMinInputChange} className="t-w-full t-min-w-0 t-rounded-md t-border t-border-gray-300 t-bg-white t-px-2.5 t-py-2 t-text-sm t-leading-6 t-tabular-nums t-transition-colors t-duration-200 focus:t-border-brand-violet focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet" aria-label={`Minimum ${title.toLowerCase()} value`} />
                                </div>
                            </div>

                            <div className="t-min-w-0">
                                <label htmlFor={maxInputId} className="t-mb-1 t-block t-text-[11px] t-font-medium t-text-[#69636f]">
                                    Max {title}
                                </label>
                                <div className="t-flex t-min-w-0">
                                    <input id={maxInputId} ref={maxInputRef} type="number" min={value[0]} max={max} step={inputStep} inputMode="numeric" enterKeyHint="done" defaultValue={value[1]} onChange={handleMaxInputChange} className="t-w-full t-min-w-0 t-rounded-md t-border t-border-gray-300 t-bg-white t-px-2.5 t-py-2 t-text-sm t-leading-6 t-tabular-nums t-transition-colors t-duration-200 focus:t-border-brand-violet focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet" aria-label={`Maximum ${title.toLowerCase()} value`} />
                                </div>
                            </div>
                        </div>

                        {/* Modern Slider */}
                        <div className="t-px-2 t-py-3 sm:t-px-3">
                            <Slider key={`${value[0]}-${value[1]}`} defaultValue={value} onChange={handleSliderChange} onChangeEnd={handleSliderChangeEnd} minValue={min} maxValue={max} step={step} className="t-w-full" aria-label={`${title} range selector`}>
                                <SliderOutput className="t-sr-only">
                                    {({
                state
              }) => `Range: ${formatValue(state.values[0] || 0)} - ${formatValue(state.values[1] || 0)}`}
                                </SliderOutput>

                                <SliderTrack className="t-relative t-h-1.5 t-w-full t-touch-none t-rounded-full t-bg-[#ded9e1]">
                                    {({
                state: state_0
              }) => <>
                                            {/* Active range indicator */}
                                            <div className="t-absolute t-top-0 t-h-1.5 t-rounded-full t-bg-brand-violet" style={{
                  left: `${state_0.getThumbPercent(0) * 100}%`,
                  width: `${(state_0.getThumbPercent(1) - state_0.getThumbPercent(0)) * 100}%`
                }} />

                                            {/* Render thumbs */}
                                            {state_0.values.map((_, i) => <SliderThumb key={i} index={i} className="t-w-6 t-h-6 sm:t-w-5 sm:t-h-5 t-bg-white t-border-2 t-border-brand-violet t-rounded-full t-shadow-lg t-cursor-pointer t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2 t-transition-all t-duration-200 hover:t-scale-110 active:t-scale-105" aria-label={`${i === 0 ? 'Minimum' : 'Maximum'} ${title.toLowerCase()} value`}>
                                                    {({
                    state: thumbState,
                    isHovered,
                    isDragging
                  }) => <>
                                                            {(isHovered || isDragging) && <div className="t-absolute t--top-12 t-left-1/2 t-transform t--translate-x-1/2 t-bg-gray-900 t-text-white t-px-2 t-py-1 t-rounded-md t-text-xs t-font-medium t-whitespace-nowrap t-z-10 t-shadow-lg">
                                                                    {formatValue(thumbState.values[i] || 0)}
                                                                    <div className="t-absolute t-top-full t-left-1/2 t-transform t--translate-x-1/2 t-w-0 t-h-0 t-border-l-2 t-border-r-2 t-border-t-4 t-border-transparent t-border-t-gray-900"></div>
                                                                </div>}
                                                        </>}
                                                </SliderThumb>)}
                                        </>}
                                </SliderTrack>
                            </Slider>
                        </div>
                    </div>}
            </div>
        </div>;
};